import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Form,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import { CheckPermission, GetUrlParams, ObjectToURL } from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/SpecialJobForm";

const Component = ({ onSetRoute, parentName, permissions, routeName }) => {
	const initialState = { data: [], pagination: {} };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "job",
			render: (value) => value?.title,
		},

		{
			title: `${Resources.date} ${Resources.start}`,
			dataIndex: "reservedAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return date;
			},
		},
		{
			title: `${Resources.date} ${Resources.end}`,
			dataIndex: "reservedUntil",
			className: "center-cell  ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return date;
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(record)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, loading, pagination, selected } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.specialJob
			.list({
				...params,
				pageSize: process.env.REACT_APP_PAGE_SIZE,
			})
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (selected) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.specialJob
					.delete(selected.id)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const query = ObjectToURL({ ...params, page: current });

		navigate(`${pathname}?${query}`);
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={`${Resources.jobs} ${Resources.special}`}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>
			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="sortBy">
							<Select
								placeholder={Resources.sort}
								options={[
									{
										label: `${Resources.date} ${Resources.start}`,
										value: "reservedAt",
									},
									{
										label: `${Resources.date} ${Resources.end}`,
										value: "reservedUntill",
									},
								]}
								onChange={handleFilter}
								optionFilterProp="label"
								showSearch
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 930 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			cities: state.provinces.map((province) => ({
				label: province.name,
				options: state.cities
					.filter((city) => Number(city.provinceId) === Number(province.id))
					.map((city) => ({
						value: city.id,
						label: city.name,
					})),
			})),
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.SPECIAL_JOB_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.SPECIAL_JOB_DELETE
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.SPECIAL_JOB_UPDATE
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
